import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Login.css';
import '../styles/Common.css';
import logban from "../assets/backdrop.png";
import { useAuth } from '../App.js';

const API_ENDPOINTS = {
  LOGIN: '/api/tour-operators/login',
  FORGOT_PASSWORD: '/api/tour-operators/forgot-password',
  VERIFY_PASSWORD_OTP: '/api/tour-operators/verify-password-otp',
  RESET_PASSWORD: '/api/tour-operators/reset-password'
};

const VALIDATION = {
  PHONE_LENGTH: 10
};

const Login = () => {
  const [formData, setFormData] = useState({
    phone: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    phone: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const navigate = useNavigate();
  const { login, isLoading: authLoading } = useAuth();

  // Countdown timer for OTP resend
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleForgotPasswordChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const sanitizePhoneInput = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
    handleInputChange(e);
  };

  const sanitizeForgotPasswordPhone = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
    handleForgotPasswordChange(e);
  };

  const validateForm = (phone, password) => {
    if (!phone || !password) {
      alert("Please fill in both fields.");
      return false;
    }

    if (phone.length !== VALIDATION.PHONE_LENGTH) {
      alert("Please enter a valid 10-digit phone number.");
      return false;
    }

    return true;
  };

  const validateForgotPassword = (step, data) => {
    if (step === 1) {
      if (!data.phone || data.phone.length !== VALIDATION.PHONE_LENGTH) {
        alert("Please enter a valid 10-digit phone number.");
        return false;
      }
    } else if (step === 2) {
      if (!data.otp || data.otp.length !== 6) {
        alert("Please enter the 6-digit OTP sent to your phone.");
        return false;
      }
    } else if (step === 3) {
      if (!data.newPassword || !data.confirmPassword) {
        alert("Please enter and confirm your new password.");
        return false;
      }
      if (data.newPassword.length < 6) {
        alert("Password must be at least 6 characters long.");
        return false;
      }
      if (data.newPassword !== data.confirmPassword) {
        alert("Passwords do not match.");
        return false;
      }
    }
    return true;
  };

  const sendForgotPasswordOTP = async (phone) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FORGOT_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send OTP');
      }

      return result;
    } catch (error) {
      throw error;
    }
  };

  const verifyPasswordOTP = async (phone, otp) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.VERIFY_PASSWORD_OTP}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, otp })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Invalid OTP');
      }

      return result;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (phone, otp, newPassword) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.RESET_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, otp, newPassword })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reset password');
      }

      return result;
    } catch (error) {
      throw error;
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!validateForgotPassword(forgotPasswordStep, forgotPasswordData)) {
      return;
    }

    setIsForgotPasswordLoading(true);

    try {
      if (forgotPasswordStep === 1) {
        await sendForgotPasswordOTP(forgotPasswordData.phone);
        setCountdown(60);
        setForgotPasswordStep(2);
        alert('OTP sent to your phone number');
      } else if (forgotPasswordStep === 2) {
        await verifyPasswordOTP(forgotPasswordData.phone, forgotPasswordData.otp);
        setForgotPasswordStep(3);
        alert('OTP verified successfully');
      } else if (forgotPasswordStep === 3) {
        await resetPassword(
          forgotPasswordData.phone, 
          forgotPasswordData.otp, 
          forgotPasswordData.newPassword
        );
        alert('Password reset successfully! You can now login with your new password.');
        setShowForgotPassword(false);
        setForgotPasswordStep(1);
        setForgotPasswordData({
          phone: '',
          otp: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('❌ Forgot password error:', error);
      alert(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setIsForgotPasswordLoading(true);
    try {
      await sendForgotPasswordOTP(forgotPasswordData.phone);
      setCountdown(60);
      alert('OTP resent successfully');
    } catch (error) {
      alert(error.message || 'Failed to resend OTP');
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      phone: '',
      otp: '',
      newPassword: '',
      confirmPassword: ''
    });
    setCountdown(0);
  };

  const performLogin = async (phone, password) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, password })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Login failed');
      }

      return result;
    } catch (error) {
      throw error;
    }
  };

  // FIXED: Login function with proper redirect
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const { phone, password } = formData;

    if (!validateForm(phone, password)) return;

    setIsLoading(true);

    try {
      const result = await performLogin(phone, password);
      
      if (result.isDemo) {
        alert('Demo login successful! Please sign up to create your own account.');
        return;
      }

      if (result.token && result.operator) {
        // Store auth data
        login(result.token, result.operator);
        console.log('✅ Login successful - redirecting to home');
        
        // Force redirect to home page
        window.location.href = '/';
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      alert(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const goToSignup = (e) => {
    e.preventDefault();
    navigate('/signup');
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="login-page">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="login-page" className="login-page">
      <div className="loginbanner">
        <img 
          src={logban} 
          className="banner-image" 
          alt="URASUB Banner" 
          loading="lazy"
        />
      </div>
      <div className="container">
        <h1 className="welcome-text slide-up">URACAB WELCOME'S YOU</h1>
        
        {!showForgotPassword ? (
          <form onSubmit={handleLoginSubmit} className="loginForm fade-in">
            <div className="input-group">
              <label htmlFor="phone">Phone Number</label>
              <input 
                type="tel" 
                id="phone" 
                maxLength="10" 
                placeholder="Enter 10-digit phone number" 
                required 
                value={formData.phone}
                onChange={handleInputChange}
                onInput={sanitizePhoneInput}
                autoComplete="tel"
                disabled={isLoading}
              />
            </div>

            <div className="input-group">
              <label htmlFor="password">Password</label>
              <input 
                type="password" 
                id="password" 
                placeholder="Enter your password" 
                required 
                value={formData.password}
                onChange={handleInputChange}
                autoComplete="current-password"
                disabled={isLoading}
              />
            </div>
            
            <div className="signin-con">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </div>

            <div className="signup-link-container">
              <p>Don't have an account? <a href="/signup" onClick={goToSignup}>Sign Up</a></p>
            </div>
            <div className="forgot-password-container">
              <button 
                type="button" 
                className="forgot-password-link"
                onClick={() => setShowForgotPassword(true)}
                disabled={isLoading}
                style={{
                  border:"none",
                  backgroundColor:"white",
                  textDecoration:"underline"
                }}
              >
                Forgot Password?
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleForgotPassword} className="loginForm fade-in">
            <h2 className="forgot-password-title">
              {forgotPasswordStep === 1 && 'Reset Your Password'}
              {forgotPasswordStep === 2 && 'Verify OTP'}
              {forgotPasswordStep === 3 && 'Create New Password'}
            </h2>

            {forgotPasswordStep === 1 && (
              <div className="input-group">
                <label htmlFor="forgot-phone">Phone Number</label>
                <input 
                  type="tel" 
                  id="forgot-phone"
                  name="phone"
                  maxLength="10" 
                  placeholder="Enter your registered phone number" 
                  required 
                  value={forgotPasswordData.phone}
                  onChange={handleForgotPasswordChange}
                  onInput={sanitizeForgotPasswordPhone}
                  autoComplete="tel"
                  disabled={isForgotPasswordLoading}
                />
              </div>
            )}

            {forgotPasswordStep === 2 && (
              <>
                <div className="input-group">
                  <label htmlFor="otp">Enter OTP</label>
                  <input 
                    type="text" 
                    id="otp"
                    name="otp"
                    maxLength="6" 
                    placeholder="Enter 6-digit OTP" 
                    required 
                    value={forgotPasswordData.otp}
                    onChange={handleForgotPasswordChange}
                    disabled={isForgotPasswordLoading}
                  />
                </div>
                <div className="otp-resend-container">
                  <button 
                    type="button" 
                    className="resend-otp-btn"
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || isForgotPasswordLoading}
                  >
                    {countdown > 0 ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>
              </>
            )}

            {forgotPasswordStep === 3 && (
              <>
                <div className="input-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input 
                    type="password" 
                    id="newPassword"
                    name="newPassword"
                    placeholder="Enter new password" 
                    required 
                    value={forgotPasswordData.newPassword}
                    onChange={handleForgotPasswordChange}
                    disabled={isForgotPasswordLoading}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input 
                    type="password" 
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Confirm new password" 
                    required 
                    value={forgotPasswordData.confirmPassword}
                    onChange={handleForgotPasswordChange}
                    disabled={isForgotPasswordLoading}
                  />
                </div>
              </>
            )}

            <div className="forgot-password-buttons">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isForgotPasswordLoading}
              >
                {isForgotPasswordLoading ? 'Processing...' : 
                 forgotPasswordStep === 1 ? 'Send OTP' :
                 forgotPasswordStep === 2 ? 'Verify OTP' : 'Reset Password'}
              </button>
              
              <button 
                type="button" 
                className="back-to-login-btn"
                onClick={handleBackToLogin}
                disabled={isForgotPasswordLoading}
              >
                Back to Login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;